import React, { useState, useEffect } from 'react';
import { cn } from '../../../utils/cn';
import Icon from '../../../components/AppIcon';

const CalendarGrid = ({ events, viewMode, currentDate, onEventClick, onEventDelete, deletingEvent, onEventDrop }) => {
  const [draggedEvent, setDraggedEvent] = useState(null);
  const [dragOverDate, setDragOverDate] = useState(null);

  const getEventColor = (eventType) => {
    const colors = {
      'availability': 'bg-success text-white',
      'booking': 'bg-primary text-white',
      'milestone': 'bg-accent text-white',
      'inspection': 'bg-warning text-white'
    };
    return colors?.[eventType] || 'bg-secondary text-white';
  };

  const handleEventClick = (event, e) => {
    e?.stopPropagation();
    
    // If event has projectId or contractorId, navigate directly
    if (event?.projectId) {
      const navEvent = new CustomEvent('openProjectDetails', {
        detail: { projectId: event?.projectId }
      });
      window.dispatchEvent(navEvent);
    } else if (event?.contractorId) {
      const navEvent = new CustomEvent('openContractorProfile', {
        detail: { contractorId: event?.contractorId }
      });
      window.dispatchEvent(navEvent);
    } else if (event?.teamMemberId) {
      // Navigate to settings/team members
      const navEvent = new CustomEvent('navigateToSettings', {
        detail: { tab: 'team' }
      });
      window.dispatchEvent(navEvent);
    } else {
      // Otherwise show event details modal
      onEventClick?.(event);
    }
  };

  // --- Drag-drop handlers ---
  const handleDragStart = (e, event) => {
    setDraggedEvent(event);
    e.dataTransfer.effectAllowed = 'move';
    e?.dataTransfer?.setData('text/plain', event?.id);
  };

  const handleDragEnd = () => {
    setDraggedEvent(null);
    setDragOverDate(null);
  };

  const handleDragOver = (e, date) => {
    e?.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverDate(date?.toDateString());
  };

  const handleDragLeave = () => {
    setDragOverDate(null);
  };

  const handleDrop = (e, targetDate) => {
    e?.preventDefault();
    setDragOverDate(null);
    if (!draggedEvent || !onEventDrop) return;

    const originalStart = new Date(draggedEvent?.startDate);
    const originalEnd = new Date(draggedEvent?.endDate);
    const durationMs = originalEnd - originalStart;

    // Preserve original time, only change the date
    const newStart = new Date(targetDate);
    newStart?.setHours(originalStart?.getHours(), originalStart?.getMinutes(), originalStart?.getSeconds(), 0);
    const newEnd = new Date(newStart.getTime() + durationMs);

    onEventDrop?.(draggedEvent, newStart?.toISOString(), newEnd?.toISOString());
    setDraggedEvent(null);
  };

  const getDaysInMonth = (date) => {
    const year = date?.getFullYear();
    const month = date?.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay?.getDate();
    const startingDayOfWeek = firstDay?.getDay();
    
    return { daysInMonth, startingDayOfWeek, year, month };
  };

  const getWeekDays = (date) => {
    const startOfWeek = new Date(date);
    startOfWeek?.setDate(date?.getDate() - date?.getDay());
    
    const days = [];
    for (let i = 0; i < 7; i++) {
      let day = new Date(startOfWeek);
      day?.setDate(startOfWeek?.getDate() + i);
      days?.push(day);
    }
    return days;
  };

  const getEventsForDate = (date) => {
    return events?.filter(event => {
      const eventStart = new Date(event?.startDate);
      const eventEnd = new Date(event?.endDate);
      const checkDate = new Date(date);
      
      checkDate?.setHours(0, 0, 0, 0);
      eventStart?.setHours(0, 0, 0, 0);
      eventEnd?.setHours(0, 0, 0, 0);
      
      return checkDate >= eventStart && checkDate <= eventEnd;
    });
  };

  const isToday = (date) => {
    const today = new Date();
    return date?.toDateString() === today?.toDateString();
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  const renderMonthView = () => {
    const { daysInMonth, startingDayOfWeek, year, month } = getDaysInMonth(currentDate);
    const days = [];
    
    // Empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days?.push(<div key={`empty-${i}`} className="min-h-24 p-2 bg-muted/30" />);
    }
    
    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dayEvents = getEventsForDate(date);
      const today = isToday(date);
      const isDropTarget = dragOverDate === date?.toDateString();
      
      days?.push(
        <div
          key={day}
          className={cn(
            "min-h-24 p-2 border border-border hover:bg-muted/50 transition-colors",
            today && "bg-primary/5 border-primary",
            isDropTarget && "bg-primary/10 border-primary border-2"
          )}
          onDragOver={(e) => handleDragOver(e, date)}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, date)}
        >
          <div className={cn(
            "text-sm font-medium mb-2",
            today ? "text-primary" : "text-foreground"
          )}>
            {day}
          </div>
          <div className="space-y-1">
            {dayEvents?.slice(0, 3)?.map((event) => (
              <button
                key={event?.id}
                draggable
                onDragStart={(e) => handleDragStart(e, event)}
                onDragEnd={handleDragEnd}
                onClick={(e) => handleEventClick(event, e)}
                className={cn(
                  "w-full text-left px-2 py-1 rounded text-xs font-medium truncate hover:opacity-80 transition-opacity cursor-grab active:cursor-grabbing",
                  getEventColor(event?.eventType),
                  draggedEvent?.id === event?.id && "opacity-50"
                )}
                title={`${event?.title} — drag to reschedule`}
              >
                {event?.title}
              </button>
            ))}
            {dayEvents?.length > 3 && (
              <div className="text-xs text-muted-foreground px-2">
                +{dayEvents?.length - 3} more
              </div>
            )}
          </div>
        </div>
      );
    }
    
    return (
      <div>
        {/* Weekday Headers */}
        <div className="grid grid-cols-7 border-b border-border">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']?.map((day) => (
            <div key={day} className="p-3 text-center text-sm font-semibold text-muted-foreground border-r border-border last:border-r-0">
              {day}
            </div>
          ))}
        </div>
        {/* Calendar Days */}
        <div className="grid grid-cols-7">
          {days}
        </div>
      </div>
    );
  };

  const renderWeekView = () => {
    const weekDays = getWeekDays(currentDate);
    
    return (
      <div className="flex">
        {weekDays?.map((date, index) => {
          const dayEvents = getEventsForDate(date);
          const today = isToday(date);
          const isDropTarget = dragOverDate === date?.toDateString();
          
          return (
            <div
              key={index}
              className={cn(
                "flex-1 border-r border-border last:border-r-0",
                isDropTarget && "bg-primary/10"
              )}
              onDragOver={(e) => handleDragOver(e, date)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, date)}
            >
              <div className={cn(
                "p-3 border-b border-border text-center",
                today && "bg-primary/5"
              )}>
                <div className="text-xs text-muted-foreground mb-1">
                  {date?.toLocaleDateString('en-US', { weekday: 'short' })}
                </div>
                <div className={cn(
                  "text-lg font-semibold",
                  today ? "text-primary" : "text-foreground"
                )}>
                  {date?.getDate()}
                </div>
              </div>
              <div className="p-2 space-y-2 min-h-96">
                <div className="space-y-2">
                  {dayEvents?.map((event) => (
                    <button
                      key={event?.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, event)}
                      onDragEnd={handleDragEnd}
                      onClick={(e) => handleEventClick(event, e)}
                      className={cn(
                        "w-full text-left px-3 py-2 rounded-lg text-sm hover:opacity-80 transition-opacity cursor-grab active:cursor-grabbing",
                        getEventColor(event?.eventType),
                        draggedEvent?.id === event?.id && "opacity-50"
                      )}
                      title={`${event?.title} — drag to reschedule`}
                    >
                      <div className="font-medium truncate">{event?.title}</div>
                      <div className="text-xs opacity-90 mt-1">
                        {formatTime(event?.startDate)}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderDayView = () => {
    const dayEvents = getEventsForDate(currentDate)?.sort((a, b) => {
      return new Date(a?.startDate) - new Date(b?.startDate);
    });
    
    return (
      <div className="space-y-3">
        {dayEvents?.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Icon name="Calendar" size={48} className="mx-auto mb-3 opacity-50" />
            <p>No events scheduled for this day</p>
          </div>
        ) : (
          dayEvents?.map((event) => (
            <div
              key={event?.id}
              className="bg-card rounded-lg shadow-sm border border-border p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={cn(
                      "px-2 py-1 rounded text-xs font-medium",
                      getEventColor(event?.eventType)
                    )}>
                      {event?.eventType?.replace('_', ' ')?.toUpperCase()}
                    </span>
                    {event?.allDay && (
                      <span className="text-xs text-muted-foreground">All Day</span>
                    )}
                  </div>
                  <h3 className="font-semibold text-foreground mb-1">{event?.title}</h3>
                  {event?.description && (
                    <p className="text-sm text-muted-foreground mb-2">{event?.description}</p>
                  )}
                  {!event?.allDay && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Icon name="Clock" size={14} />
                      <span>
                        {formatTime(event?.startDate)} - {formatTime(event?.endDate)}
                      </span>
                    </div>
                  )}
                  {event?.location && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground mt-2">
                      <Icon name="MapPin" size={14} />
                      <span>{event?.location}</span>
                    </div>
                  )}
                  {event?.teamMemberId && (
                    <div className="mt-3 pt-3 border-t border-border">
                      <p className="text-xs text-muted-foreground mb-1">Team Member</p>
                      <button
                        onClick={(e) => {
                          e?.stopPropagation();
                          const navEvent = new CustomEvent('navigateToSettings', {
                            detail: { tab: 'team' }
                          });
                          window.dispatchEvent(navEvent);
                        }}
                        className="text-sm text-primary hover:underline flex items-center gap-1"
                      >
                        View Team Member
                        <Icon name="ExternalLink" size={12} />
                      </button>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => {
                      e?.stopPropagation();
                      handleEventClick(event, e);
                    }}
                    className="p-2 hover:bg-muted rounded-lg transition-colors"
                    title="View details"
                  >
                    <Icon name="Eye" size={18} className="text-muted-foreground" />
                  </button>
                  <button
                    onClick={(e) => {
                      e?.stopPropagation();
                      onEventDelete?.(event?.id);
                    }}
                    disabled={deletingEvent === event?.id}
                    className="p-1 hover:bg-destructive/20 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Delete event"
                  >
                    {deletingEvent === event?.id ? (
                      <Icon name="Loader2" size={12} className="text-destructive animate-spin" />
                    ) : (
                      <Icon name="Trash2" size={12} className="text-destructive" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    );
  };

  const renderListView = () => {
    const sortedEvents = [...events]?.sort((a, b) => 
      new Date(a?.startDate) - new Date(b?.startDate)
    );

    return (
      <div className="space-y-3">
        {sortedEvents?.map((event) => (
          <button
            key={event?.id}
            onClick={(e) => handleEventClick(event, e)}
            className="w-full bg-card border border-border rounded-lg p-4 hover:bg-muted/50 transition-colors text-left"
          >
            <div className="flex items-start gap-4">
              <div className={cn(
                "w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0",
                getEventColor(event?.eventType)
              )}>
                <Icon name="Calendar" size={20} />
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-foreground mb-1">{event?.title}</h4>
                <p className="text-sm text-muted-foreground mb-2">{event?.description}</p>
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Icon name="Calendar" size={12} />
                    {new Date(event?.startDate)?.toLocaleDateString()}
                  </span>
                  <span className="flex items-center gap-1">
                    <Icon name="Clock" size={12} />
                    {formatTime(event?.startDate)}
                  </span>
                  {event?.projectId && (
                    <span className="flex items-center gap-1 text-primary">
                      <Icon name="Briefcase" size={12} />
                      Linked to Project
                    </span>
                  )}
                  {event?.contractorId && (
                    <span className="flex items-center gap-1 text-accent">
                      <Icon name="User" size={12} />
                      Linked to Contractor
                    </span>
                  )}
                </div>
              </div>
              {onEventDelete && (
                <button
                  onClick={(e) => {
                    e?.stopPropagation();
                    onEventDelete?.(event?.id);
                  }}
                  className="p-2 hover:bg-destructive/10 rounded-lg text-destructive transition-colors"
                >
                  <Icon name="Trash2" size={16} />
                </button>
              )}
            </div>
          </button>
        ))}
      </div>
    );
  };

  return (
    <div className="bg-card rounded-xl shadow-sm border border-border overflow-hidden">
      {viewMode === 'month' && renderMonthView()}
      {viewMode === 'week' && renderWeekView()}
      {viewMode === 'day' && renderDayView()}
    </div>
  );
};

export default CalendarGrid;